import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Mail, Loader2, AlertTriangle, Send, CalendarClock } from "lucide-react";

const SEV = {
  high: { dot: "#B54747", chip: "bg-[#B54747]/10 text-[#B54747]" },
  medium: { dot: "#D5A44A", chip: "bg-[#D5A44A]/15 text-[#8A6A1F]" },
  low: { dot: "#156F52", chip: "bg-[#156F52]/10 text-[#156F52]" },
};
const ICON = { contract: "Contract", budget: "Budget", intake: "Intake", invoice: "Invoice" };
const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

export default function AlertsDigest() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState(null);
  const [recipient, setRecipient] = useState(user?.email || "");
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const canSend = can(user?.role, "alerts:send");

  useEffect(() => {
    api.get("/alerts").then((r) => setAlerts(r.data));
    api.get("/digest/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const sendDigest = async () => {
    setSending(true);
    setPreview(null);
    try {
      const { data } = await api.post("/digest/send", { recipient: recipient || undefined });
      setPreview(data.body);
      toast.success(`Weekly digest emailed to ${data.recipient}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send digest");
    } finally {
      setSending(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await api.put("/digest/settings", {
        enabled: settings.enabled, day: settings.day, hour: settings.hour,
        recipient: settings.recipient || undefined,
      });
      toast.success(settings.enabled ? "Automated weekly digest is ON" : "Automated digest turned off");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Stay ahead</p>
        <h1 className="font-display text-4xl font-light mt-1">Alerts & Weekly Digest</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Proactive warnings before money leaks — contract deadlines, budget overspend and dropped leads — plus an
          AI-written weekly briefing emailed to your managing partner.
        </p>
      </div>

      {/* Digest sender */}
      <div className="border border-zinc-200 bg-white p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Mail className="h-5 w-5 text-[#174A7E]" />
          <h3 className="font-display text-2xl">Email the weekly briefing</h3>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-xs uppercase tracking-widest font-mono-data text-zinc-500">Recipient</Label>
            <Input
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              data-testid="digest-recipient-input"
              className="mt-1 rounded-none"
            />
          </div>
          {canSend ? (
          <Button
            onClick={sendDigest}
            disabled={sending}
            data-testid="send-digest-button"
            className="bg-[#0B1F33] text-white rounded-none h-10 px-6"
          >
            {sending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending…</>
            ) : (
              <><Send className="h-4 w-4 mr-2" /> Send digest now</>
            )}
          </Button>
          ) : (
            <p className="text-xs text-zinc-500 self-end pb-2" data-testid="digest-no-perm">Only Owners &amp; Marketing Directors can send the digest.</p>
          )}
        </div>
        {preview && (
          <div className="mt-5 border border-zinc-100 bg-zinc-50 p-4 text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed" data-testid="digest-preview">
            {preview}
          </div>
        )}
      </div>

      {canSend && settings && (
        <div className="border border-zinc-200 bg-white p-6 mb-6" data-testid="digest-settings-card">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-[#156F52]" />
              <h3 className="font-display text-2xl">Automated weekly digest</h3>
            </div>
            <Switch checked={settings.enabled} onCheckedChange={(v) => setSettings({ ...settings, enabled: v })} data-testid="digest-enabled-switch" />
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-data text-zinc-500">Day</Label>
              <Select value={String(settings.day)} onValueChange={(v) => setSettings({ ...settings, day: parseInt(v) })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="digest-day-select"><SelectValue /></SelectTrigger>
                <SelectContent>{DAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-data text-zinc-500">Hour (UTC)</Label>
              <Select value={String(settings.hour)} onValueChange={(v) => setSettings({ ...settings, hour: parseInt(v) })}>
                <SelectTrigger className="mt-1 rounded-none" data-testid="digest-hour-select"><SelectValue /></SelectTrigger>
                <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{String(h).padStart(2, "0")}:00</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest font-mono-data text-zinc-500">Recipient</Label>
              <Input value={settings.recipient || ""} onChange={(e) => setSettings({ ...settings, recipient: e.target.value })} data-testid="digest-settings-recipient" className="mt-1 rounded-none" />
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between gap-3">
            <p className="text-xs text-zinc-500">
              {settings.enabled
                ? `Sends every ${DAYS[settings.day]} at ${String(settings.hour).padStart(2, "0")}:00 UTC.`
                : "Turn on to email the briefing automatically each week."}
              {settings.last_sent_at && ` Last sent ${new Date(settings.last_sent_at).toLocaleString()}.`}
            </p>
            <Button onClick={saveSettings} disabled={savingSettings} className="bg-[#0B1F33] text-white rounded-none shrink-0" data-testid="save-digest-settings-button">
              {savingSettings && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save schedule
            </Button>
          </div>
        </div>
      )}

      {/* Alerts */}
      <div className="border border-zinc-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Bell className="h-5 w-5 text-[#D5A44A]" />
          <h3 className="font-display text-2xl">Open alerts</h3>
        </div>
        {!alerts && <Skeleton className="h-24" />}
        {alerts && alerts.length === 0 && (
          <p className="text-sm text-[#156F52]">No open alerts. Nothing is leaking right now.</p>
        )}
        <div className="space-y-2">
          {alerts?.map((a, i) => (
            <div
              key={i}
              className="flex items-center gap-3 border border-zinc-100 px-4 py-3"
              data-testid={`alert-${i}`}
            >
              <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: SEV[a.severity]?.dot }} />
              <AlertTriangle className="h-4 w-4 text-zinc-400 shrink-0" />
              <p className="text-sm flex-1">{a.message}</p>
              <span className={`px-2 py-0.5 text-[10px] font-mono-data uppercase ${SEV[a.severity]?.chip}`}>
                {ICON[a.type] || a.type}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
